import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react';
import { MaxQueue } from './priority-queue';

/**
 * Provider: TaskQueueProvider
 * Purpose:
 * Manages multiple priority-based task queues for asynchronous operations across the application.
 * Provides a centralized way to queue, execute, and monitor tasks with different priorities.
 * 
 * How It Works:
 * - Tasks are added to named queues with optional priorities
 * - Each queue processes tasks sequentially based on priority
 * - Tasks can be cancelled and their status monitored
 * - Provides abort signals to allow task cancellation
 * - Maintains task state transitions (queued -> running -> completed/error/cancelled)
 * 
 * Expected Results:
 * - Tasks execute in priority order within their named queues
 * - Higher priority tasks will be executed before lower priority ones
 * - Task status updates are available via callbacks and status queries
 * - Cancelled tasks are properly cleaned up
 * - Concurrent queues operate independently
 * 
 * Usage:
 * const { addTask, getTaskStatus } = useTaskQueue();
 * const { cancel } = addTask(
 *   async () => { ... },      // Task function
 *   'queueName',              // Optional queue name
 *   (status) => { ... },      // Optional status callback
 *   1                         // Optional priority
 * );
 */


type TaskId = string;
type QueueName = string;

interface Task {
  fn: (signal?: AbortSignal) => Promise<unknown>;
  id: TaskId;
  queueName: QueueName;
  priority: number;
}

type TaskStatus = 'queued' | 'running' | 'completed' | 'error' | 'cancelled';

export interface TaskInfo {
  status: TaskStatus;
  error?: Error;
  result?: unknown;
}

type StatusCallback = (info: TaskInfo) => void;

interface QueueContextValue {
  addTask: (
    task: () => Promise<unknown>,
    queueName?: QueueName,
    statusCallback?: StatusCallback,
    priority?: number
  ) => { cancel: () => void };
  getTaskStatus: (taskId: TaskId) => TaskInfo | null;
}

const QueueContext = createContext<QueueContextValue | undefined>(undefined);

// Helper function to generate unique task IDs
const generateTaskId = (existingTasks: Map<TaskId, TaskInfo>): TaskId => {
  const maxAttempts = existingTasks.size * 10 || 100;
  let attempts = 0;

  while (attempts < maxAttempts) {
    const id = Math.floor(Math.random() * maxAttempts).toString();
    if (!existingTasks.has(id)) {
      return id;
    }
    attempts++;
  }

  return `task-${existingTasks.size}`;
};

export const TaskQueueProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Using MaxQueue for priority-based task management
  const queuesRef = useRef<Map<QueueName, MaxQueue<Task>>>(new Map());
  const taskStatusRef = useRef<Map<TaskId, TaskInfo>>(new Map());
  const statusCallbacksRef = useRef<Map<TaskId, StatusCallback>>(new Map());
  const isProcessingRef = useRef<Set<QueueName>>(new Set());
  const abortControllersRef = useRef<Map<TaskId, AbortController>>(new Map());
  const pendingTasksRef = useRef<Map<QueueName, Task[]>>(new Map());

  /**
   * Updates the status of a task and notifies any registered status callbacks
   * 
   * @param taskId - Unique identifier for the task
   * @param info - Object containing task status information:
   *   - status: Current state of the task ('queued'|'running'|'completed'|'error'|'cancelled')
   *   - error?: Optional error object if task failed
   *   - result?: Optional result value if task completed successfully
   */
  const updateTaskStatus = useCallback((taskId: TaskId, info: TaskInfo) => {
    taskStatusRef.current.set(taskId, info);
    const callback = statusCallbacksRef.current.get(taskId);
    if (callback) {
      callback(info); // Only pass the TaskInfo object to the callback
    }
  }, []);

  /**
   * Removes a task from the queue and updates its status to cancelled
   * 
   * @param taskId - Unique identifier for the task to cancel
   * @param queueName - Name of the queue containing the task
   */
  const removeTask = useCallback((taskId: TaskId, queueName: QueueName) => {
    const queue = queuesRef.current.get(queueName);
    if (queue) {
      const tasks = queue.getElements();
      const filteredTasks = tasks.filter((task) => task.element.id !== taskId);
      // Recreate the queue without the removed task
      const newQueue = new MaxQueue<Task>();
      filteredTasks.forEach((task) => {
        newQueue.add(task.element, task.priority);
      });

      // Update the queue reference
      queuesRef.current.set(queueName, newQueue);
    }
  }, []);

  /**
   * Processes tasks in a queue sequentially based on priority
   * 
   * @param queueName - Name of the queue to process
   */
  const processQueue = useCallback(
    async (queueName: QueueName) => {
      const queue = queuesRef.current.get(queueName);
      if (
        !queue ||
        isProcessingRef.current.has(queueName) ||
        queue.length() === 0
      )
        return;

      isProcessingRef.current.add(queueName);

      try {
        while (queue.length() > 0) {
          const task = queue.remove()?.element; // Get the highest-priority task
          if (!task) continue;

          if(taskStatusRef.current.get(task.id)?.status !== 'cancelled') updateTaskStatus(task.id, { status: 'running' });

          try {
            // Protection against race condition
            // A task could be cancelled right after it's removed from the queue but before it starts executing (between queue.remove() and execution)
            const abortController = abortControllersRef.current.get(task.id);
            if (!abortController || abortController.signal.aborted) {
              continue; // Skip if already aborted
            }

            const result = await task.fn(abortController?.signal);
            updateTaskStatus(task.id, { status: 'completed', result });
          } catch (error) {
            updateTaskStatus(task.id, {
              status: 'error',
              error: error instanceof Error ? error : new Error(String(error)),
            });
          }
        }
      } finally {
        isProcessingRef.current.delete(queueName);
      }
    },
    [updateTaskStatus]
  );

  /**
   * Adds a task to a queue with optional priority and status callback
   * 
   * @param taskFn - Function to be executed as the task
   * @param queueName - Name of the queue to add the task to (default: 'default')
   * @param statusCallback - Optional callback function to receive task status updates
   * @param priority - Priority of the task (default: 0)
   * @returns An object with a cancel method to cancel the task
   */
  const addTask = useCallback(
    (
      taskFn: () => Promise<unknown>,
      queueName: QueueName = 'default',
      statusCallback?: StatusCallback,
      priority: number = 0
    ) => {
      const taskId = generateTaskId(taskStatusRef.current);

      if (!queuesRef.current.has(queueName)) {
        queuesRef.current.set(queueName, new MaxQueue<Task>());
      }

      const task: Task = { fn: taskFn, id: taskId, queueName, priority };

      // Create and store an AbortController for the task
      const abortController = new AbortController();
      abortControllersRef.current.set(taskId, abortController);

      if (statusCallback) {
        statusCallbacksRef.current.set(taskId, statusCallback);
      }
      
      updateTaskStatus(taskId, { status: 'queued' });
      
      // Add task to pending tasks
      const queue = queuesRef.current.get(queueName)!;
      queue.add(task, priority);

      // Process the queue after a small delay to allow for multiple tasks to be added
      setTimeout(() => {
        processQueue(queueName);
      }, 1);

      return {
        cancel: () => {
          const controller = abortControllersRef.current.get(taskId);
          if (controller) {
            controller.abort();
            abortControllersRef.current.delete(taskId);
          }
          removeTask(taskId, queueName);
          updateTaskStatus(taskId, { status: 'cancelled' });
        },
      };
    },
    [processQueue, updateTaskStatus, removeTask]
  );

  /**
   * Retrieves the status of a task by its unique identifier
   * 
   * @param taskId - Unique identifier for the task
   * @returns The status of the task or null if not found
   */
  const getTaskStatus = useCallback((taskId: TaskId): TaskInfo | null => {
    return taskStatusRef.current.get(taskId) || null;
  }, []);

  return (
    <QueueContext.Provider value={{ addTask, getTaskStatus }}>
      {children}
    </QueueContext.Provider>
  );
};

export const useTaskQueue = () => {
  const context = useContext(QueueContext);
  if (!context) {
    throw new Error('useTaskQueue must be used within a TaskQueueProvider');
  }
  return context;
};